import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

export default function EngineerFaults() {
  const { data, ready } = useData();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');

  const categories = ['all', ...new Set((data.faults || []).map((f) => f.category).filter(Boolean))];

  const filtered = (data.faults || []).filter((fault) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesTerm = !term ||
      (fault.name || '').toLowerCase().includes(term) ||
      (fault.fault_code || '').toLowerCase().includes(term) ||
      (fault.equipment_type || '').toLowerCase().includes(term) ||
      (fault.common_causes || '').toLowerCase().includes(term);
    const matchesCat = category === 'all' || fault.category === category;
    return matchesTerm && matchesCat;
  });

  const priorityClass = (p) => ({
    low: 'status-green',
    medium: 'status-yellow',
    high: 'status-orange',
    critical: 'status-red',
  }[p] || 'status-gray');

  if (!ready) return <div className="page-container"><Skeleton rows={6} /></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Faults Database</h1>
        <p>Troubleshooting guides and fault codes</p>
      </div>

      <div className="filter-bar">
        <div className="search-input">
          <SafeIcon icon={Icons.Search} size={16} />
          <input
            type="text"
            placeholder="Search faults, codes, equipment…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search faults"
          />
        </div>
      </div>

      <div className="filter-bar">
        {categories.map((cat) => (
          <button
            key={cat}
            className={`filter-button ${category === cat ? 'active' : ''}`}
            onClick={() => setCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No faults found"
          message={searchTerm ? `Nothing matches "${searchTerm}"` : 'The faults database is empty'}
          icon={Icons.AlertTriangle}
        />
      ) : (
        <div className="faults-grid">
          {filtered.map((fault) => (
            <div
              key={fault.id}
              className="fault-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`${fault.id}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`${fault.id}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="job-header">
                <span className="fault-code">{fault.fault_code || '—'}</span>
                <span className={`job-status ${priorityClass(fault.priority)}`}>
                  {fault.priority}
                </span>
              </div>
              <h3>{fault.name}</h3>
              {fault.equipment_type && <p>{fault.equipment_type}</p>}
              {fault.description && (
                <p style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {fault.description}
                </p>
              )}
              <div className="part-meta">
                <span className="doc-meta">
                  {fault.estimated_time_minutes ? `${fault.estimated_time_minutes} min est.` : ''}
                </span>
                <SafeIcon icon={Icons.ChevronRight} size={16} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
